import { Dispatch, SetStateAction, useMemo, useState } from 'react';

import { isEmpty, isObject, isString, isUndefined } from '@neslotech/utils';

import * as rules from './ValidationRules';
import ValidationMessages from './ValidationMessages';

type ValidatorFunction<T> = (value: unknown, form: T) => string;
interface CustomValidator {
  validator: string;
  message: string;
}

interface Validators<T> {
  [extraProps: string]: [ValidatorFunction<T>] | string[] | [CustomValidator];
}

export interface ValidationRules<T> {
  validates: Validators<T>;
}

interface ErrorMessages {
  [extraProps: string]: string;
}

export interface Form {
  [extraProps: string]: any;

  valid: boolean;
  messages: ErrorMessages;
}

export function useFormState<T extends Form>(
  initialState: T | undefined,
  validationRules: ValidationRules<T>
): [T, Dispatch<SetStateAction<T>>] {
  const [form, setForm] = useState<T>(initialState);
  const validate = useMemo(() => createValidator<T>(validationRules), [validationRules]);
  return isUndefined(form) ? [form, setForm] : [validate(form), setForm];
}

/**
 * Creates a function that validates according to the provided form rules
 */
type Validator<T> = (form: T) => T;
export function createValidator<T>(def: ValidationRules<T>): Validator<T> {
  let _validators: Validators<T>;
  let _validatables: string[];

  const createFieldValidatorCache = (): void => {
    _validators = {};
    _validatables = Object.keys(def.validates);
    // Create and cache validators from rules
    _validatables.forEach((field) => {
      _validators[field] = def.validates[field].map((rule) => createFieldValidator(def, rule));
    });
  };

  /**
   * Returns the first message it finds by running through the provided field's validators.
   * Returns nothing if all validations pass
   */
  function getMessage<T>(field: string, form: T): string {
    const validators = _validators[field];
    for (const validator of validators) {
      const validate: ValidatorFunction<T> = validator as unknown as ValidatorFunction<T>;
      const message: string | boolean = validate(form[field], form);
      if (message) {
        return message;
      }
    }

    return undefined;
  }

  /**
   * Returns all validation messages for the provided form
   */
  function getMessages<T>(form: T): ErrorMessages {
    const messages: ErrorMessages = {};
    for (const field of _validatables) {
      const message: string = getMessage(field, form);

      if (message) {
        messages[field] = message;
      }
    }
    return messages;
  }

  /**
   * Returns the result of validating the provided form. The result
   * consists of the form as is, messages by field name, and a valid flag
   */
  return function validate<T>(form: T): T {
    // Creation of cache is deferred
    if (!_validators) {
      createFieldValidatorCache();
    }

    const messages: ErrorMessages = getMessages(form);
    return {
      ...form,
      messages,
      valid: isEmpty(Object.entries(messages))
    };
  };
}

export function createFieldValidator<T>(
  ruleContextObject: ValidationRules<T>,
  rule: string | CustomValidator | ValidatorFunction<T>
) {
  const isStringRule = isString(rule);
  let validatorName, message: string;
  if (isStringRule) {
    validatorName = rule as string;
    message = ValidationMessages[rule as string]; // If it's not a custom rule, then we will find a message here
  } else if (isObject(rule)) {
    validatorName = (rule as CustomValidator).validator;
    message = (rule as CustomValidator).message;
  } else {
    throw new Error('Field validator was neither a String | Object: ' + JSON.stringify(rule));
  }

  let validator = ruleContextObject[validatorName];
  const isCustomRule = !!validator;
  if (isCustomRule) {
    validator = validator.bind(ruleContextObject);
  } else {
    validator = rules[validatorName];
  }

  if (isStringRule && isCustomRule) {
    // These are full validators that returns a message when invalid, so we use them as is
    return validator;
  }

  if (!message) {
    throw new Error('No associated message was found for field validator: ' + JSON.stringify(rule));
  }

  return function validateField(value: unknown, form: T): string {
    // These are primitive validators that return true or false, so we need to make them whole by returning the
    // associated message when invalid, and nothing when valid
    return validator(value, form) ? undefined : message;
  };
}
